import { demoCopy } from "./demo-copy";
import React, { createContext, useContext, useEffect, useState } from "react";

export const content = {
  en: {
    hero: {
      eyebrow: "Buskers on the Table · Where hospitality meets live entertainment",
      h1a: "Turn Empty Tables Into ",
      h1accent: "Revenue & Entertainment",
      tagline: "The first AI-powered hospitality workforce built from creators, performers and entertainers.",
      sub: "Buskers on the Table transforms artists into hospitality talent — increasing venue revenue, guest engagement and social reach.",
      audience: "Built for buskers, singers, performers, TikTok creators, YouTubers and digital entertainers.",
      qrPre: "Every performer wears a ",
      qrBold: "personal QR jersey",
      qrPost: " — one scan lets guests tip, follow, order & book.",
      metrics: [
        { value: "1,250+", label: "Artists onboarded" },
        { value: "85+", label: "Venues activated" },
        { value: "€42k", label: "Tips processed" },
      ],
      ctaVenue: "Join as a Venue",
      ctaArtist: "Join as an Artist",
    },
    problem: {
      eyebrow: "The Problem",
      title: "Hospitality is under-staffed, under-engaged and under-monetised.",
      subtitle: "Venues invest fortunes in ambience yet capture almost none of the value their live moments create.",
      cards: [
        { t: "Hospitality staff shortage", d: "Floors run lean. Guests wait longer, feel less looked-after, and spend less per visit.", stat: "1 in 3", cap: "hospitality roles unfilled" },
        { t: "Low customer engagement", d: "Diners scroll their phones instead of connecting with the room — repeat visits and loyalty stall.", stat: "-42%", cap: "dwell-time vs a decade ago" },
        { t: "Limited monetization from live music", d: "Live acts are a cost centre: expensive to book, impossible to measure, no upside from the atmosphere they create.", stat: "£0", cap: "revenue tracked per set" },
      ],
      infoEyebrow: "The Problem. The Opportunity.",
      infoCaption: "One platform that turns the problem into value for everyone — artists, restaurants and guests.",
    },
    how: {
      eyebrow: "How It Works",
      title: "From request to revenue in four steps.",
      steps: [
        { t: "Restaurants request staff & performers", d: "Venues post the shifts, styles and vibe they need — from a solo singer to a full floor team." },
        { t: "Buskers get matched through AI", d: "Our AI pairs each venue with the right trained entertainers by style, language, rating and availability." },
        { t: "Artists perform + support the floor", d: "Trained hybrid talent delivers live moments and helps hospitality service — entertainment and floor support in one." },
        { t: "Guests scan the artist's QR jersey", d: "The QR printed on every performer's uniform lets guests order, tip, follow and book in one tap — capturing every moment as revenue." },
      ],
      calloutEyebrow: "The core innovation",
      calloutPre: "Performers become hybrid hospitality talent: ",
      calloutAccent: "entertainment + service.",
      calloutSub: "Artists don't just perform — they support the floor, engage guests and lift service, all at once.",
    },
    qr: {
      eyebrow: "Core Innovation · The QR Jersey",
      title: "One shirt. Infinite engagement.",
      subtitle: "Every busker, singer and creator wears a branded uniform with a personal QR code. Guests scan it straight off the shirt — turning each performer into a walking, monetisable touchpoint.",
      unlockTitle: "One scan unlocks",
      unlocks: ["Restaurant digital menu", "Order food & drinks", "Tip the artist instantly", "Follow social profiles", "Book artist for events", "Sponsor & partner offers"],
      streamsTitle: "Monetisation streams it unlocks",
      streams: ["Venue revenue growth", "Digital tipping", "Artist bookings", "Sponsor ad revenue"],
    },
    solution: {
      eyebrow: "The Solution",
      title: "One ecosystem that turns talent into revenue.",
      intro: "Buskers on the Table trains street performers, singers and creators into floor-ready hospitality entertainers — then connects them to venues through a single, measurable platform.",
      points: [
        ["Venues", "book vetted entertainers by style, language and availability in seconds — matched by AI."],
        ["Artists", "get paid sets, digital tips, private bookings and a growing audience via their personal QR."],
        ["Guests", "scan, follow, tip, order and book — turning ambience into engagement and cash."],
        ["Every moment", "is tracked: scans, tips, follows, reach and ROI in one dashboard."],
      ],
      cards: [
        { t: "AI Matching", d: "Right act, right room, every night." },
        { t: "QR Jersey", d: "Wearable QR on every uniform — the tip & follow engine guests can't miss." },
        { t: "Live Analytics", d: "Scans, tips, reach & ROI, in real time." },
        { t: "New Revenue", d: "Tips, bookings & higher covers." },
      ],
      benefitsTitle: "Benefits for everyone",
      groups: [
        { label: "For Venues", items: [{ t: "Fill quiet nights", d: "Turn slow shifts into experiences guests talk about." }, { t: "New revenue lines", d: "Tips, private bookings and higher covers per table." }, { t: "Free social reach", d: "Creators promote your venue to their followers." }] },
        { label: "For Artists", items: [{ t: "Earn on every set", d: "Fees + digital tips + private event bookings." }, { t: "Grow your audience", d: "Your QR converts guests into followers instantly." }, { t: "Build a reputation", d: "Collect venue reviews and guest feedback." }] },
        { label: "For Guests", items: [{ t: "Follow instantly", d: "Instagram, TikTok, YouTube & Spotify in one tap." }, { t: "Tip the moment", d: "Reward the performance without cash." }, { t: "Book for events", d: "Hire your favourite act for private nights." }] },
      ],
    },
    business: {
      eyebrow: "Business Model",
      title: "Three revenue streams. One flywheel.",
      subtitle: "Recurring software revenue, compounded by transactional take-rate on every tip and booking the platform enables.",
      cards: [
        { tag: "Recurring", t: "SaaS subscriptions", d: "Tiered monthly plans for venues — dashboards, AI matching, analytics and QR tools.", metric: "£49–£299", cap: "per venue / month" },
        { tag: "Transactional", t: "Commission on tips", d: "A small take-rate on every digital tip guests send to artists through the platform.", metric: "5%", cap: "per tip" },
        { tag: "Transactional", t: "Commission on bookings", d: "Platform fee on every artist booking and private event closed on Buskers.", metric: "15%", cap: "per booking" },
      ],
      captionPre: "Every venue drives more artists → more QR scans → more tips & bookings → ",
      captionAccent: "more platform revenue.",
    },
    whyWin: {
      eyebrow: "Why We Win",
      title: "One platform solving four problems at once.",
      subtitle: "No competitor combines staffing, entertainment, social reach and sponsorship in a single measurable product.",
      pillars: [
        { t: "Solves staffing shortage", d: "Trained entertainers double as floor support on the nights venues need it most.", metric: "-30%", cap: "floor pressure on peak shifts" },
        { t: "Creates entertainment", d: "Live moments guests remember — turning ordinary covers into an experience.", metric: "+35%", cap: "guest dwell-time per visit" },
        { t: "Generates social exposure", d: "Every artist promotes the venue to their followers, and every QR converts guests into fans.", metric: "12x", cap: "organic social reach" },
        { t: "Unlocks sponsor revenue", d: "Brands advertise inside each artist's digital page — a revenue stream no venue had before.", metric: "New £", cap: "sponsor income per venue" },
      ],
      aiTitle: "Powered by our AI advantage",
      ai: [
        { t: "AI Performer Scoring", d: "Grade artists on engagement, tips and social lift.", status: "Live" },
        { t: "AI Demand Prediction", d: "Forecast the nights and acts that maximise revenue.", status: "Beta" },
        { t: "AI Matching Engine", d: "Pair the right artist with the right room, always.", status: "Live" },
      ],
      aiNote: "Our proprietary data — scans, tips, follows and bookings — trains models no competitor can replicate.",
    },
    scale: {
      eyebrow: "Why This Can Scale Globally",
      title: "Worldwide demand. Infinite supply. Perfect timing.",
      subtitle: "We sit where three booming markets converge — and every ingredient for a global platform already exists.",
      cards: [
        { m: "$4.7T", t: "Global hospitality", d: "15M+ restaurants, bars & hotels seeking differentiation and higher spend." },
        { m: "$250B", t: "Creator economy", d: "Millions of performers monetising real-world moments — and exploding fast." },
        { m: "$35B", t: "Live entertainment", d: "Demand for live experiences rebounding faster than any leisure category." },
        { m: "Next wave", t: "AI-powered hospitality", d: "AI matching & prediction is exactly where hospitality is heading next." },
      ],
      bannerLabel: "Serviceable market — hospitality venues with live programming, globally",
      bannerTitle: "A category with no dedicated platform. Until now.",
      bannerValue: "$18B+",
      disclaimer: "Market figures are illustrative estimates for demonstration purposes.",
    },
    cta: {
      eyebrow: "Book a Demo",
      title: "The Future of Hospitality Starts Here.",
      sub: "Buskers on the Table is redefining how restaurants attract guests, engage audiences and create revenue.",
      book: "Book a Demo",
      artist: "Join as an Artist",
    },
    values: {
      title: "Built as a complete growth engine",
      subtitle: "Three systems working as one — turning live moments into measurable revenue.",
      cards: [
        { t: "AI-Powered Platform", d: "Smart matching, performance scoring and demand prediction pair the right talent with the right room, every night." },
        { t: "Digital Tipping Engine", d: "One QR scan lets guests tip instantly via Apple Pay, Google Pay or card — new income on every set." },
        { t: "Social Growth System", d: "Every performer converts guests into followers, compounding organic reach for artists and venues." },
      ],
    },
    whyNow: {
      eyebrow: "Why Now",
      title: "The moment for hospitality entertainment is now.",
      problemTitle: "The Problem",
      restaurantsLabel: "Restaurants face",
      restaurants: ["Staff shortages", "Lower guest engagement", "Rising operational costs"],
      artistsLabel: "Artists face",
      artists: ["Unstable income", "Limited visibility", "No structured career path"],
      solutionTitle: "The Solution",
      solutionLabel: "Buskers on the Table combines",
      solutions: ["Hospitality", "Live Entertainment", "AI Technology", "Social Growth"],
      statement1: "Turn service staff into revenue generators.",
      statement2: "Turn performers into scalable hospitality assets.",
    },
    forWho: {
      eyebrow: "For Who",
      title: "Built for everyone who shapes the guest experience.",
      cards: [
        { t: "Restaurants & Venues", d: "Increase revenue, improve customer engagement, and transform the hospitality experience." },
        { t: "Artists & Performers", d: "Generate income, build your audience, and grow your digital presence." },
        { t: "Hospitality Groups", d: "Deploy scalable, entertainment-driven growth across multiple venues." },
      ],
    },
    leadForm: {
      ctaVenue: "Join as a Venue", ctaArtist: "Join as an Artist", requestDemo: "Request a Demo", bookCall: "Book a Call",
      titleVenue: "Join as a Venue", titleArtist: "Join as an Artist", titleDemo: "Request a Demo", titleCall: "Book a Call",
      subtitle: "Tell us a little about you and our team will be in touch.",
      fullName: "Full Name", email: "Email", phone: "Phone (optional)", company: "Company / Venue Name",
      role: "Role", roleVenue: "Venue", roleArtist: "Artist", roleInvestor: "Investor", roleGroup: "Hospitality Group",
      country: "Country", message: "Message", messagePlaceholder: "How can we help?",
      submit: "Submit", submitting: "Submitting…",
      successTitle: "Thank you.", successMsg: "Our team will contact you shortly.", close: "Close",
      errRequired: "Please fill in your name and email.", errEmail: "Please enter a valid email.", errSubmit: "Something went wrong. Please try again.",
    },
    dash: {
      r: { eyebrow: "Restaurant Dashboard", bookings: "Bookings", qrScans: "QR Scans", tipsGenerated: "Tips Generated", socialReach: "Social Reach",
        confirmed: "confirmed", thisMonth: "This month", guestTips: "Guest tips", impressions: "Estimated impressions",
        aiMatching: "AI Smart Matching", runMatch: "Run AI Match", browseArtists: "Browse Artists",
        allStyles: "All styles", allCities: "All cities", yourBookings: "Your Bookings", noBookings: "No bookings yet.", book: "Book", seats: "seats" },
      a: { eyebrow: "Artist Dashboard", welcome: "Welcome back", personalQr: "Your Personal QR", copy: "Copy", preview: "Preview",
        aiScore: "AI Performance Score", analyseMe: "Analyse me", totalEarnings: "Total Earnings", tipsReceived: "Tips Received",
        followersGained: "Followers Gained", rating: "Rating", followerGrowth: "Follower growth", aiBio: "AI Bio Generator",
        generateBio: "Generate new bio", bookingRequests: "Booking Requests", noRequests: "No booking requests.", accept: "Accept",
        decline: "Decline", recentTips: "Recent Tips", noTips: "No tips yet.", reviews: "Reviews", noReviews: "No reviews yet." },
      admin: { eyebrow: "Admin Console", title: "Platform Overview", venues: "Venues", artists: "Artists", bookings: "Bookings",
        grossBookings: "Gross Bookings", platformRevenue: "Platform Revenue", pendingApprovals: "Pending Approvals",
        allReviewed: "All profiles reviewed.", approve: "Approve", reject: "Reject", topArtists: "Top-performing artists (by tips)",
        byCity: "Bookings by city", mostActive: "Most active venues", leaderboard: "Leaderboard", bookingsWord: "bookings" },
    },
    founder: {
      badgeLine: "Built by hospitality, for hospitality.",
      badgeRole: "CEO & Founder",
      name: "Aaron Rutigliano",
      eyebrow: "Meet the Founder",
      role: "CEO & Founder · Founder of Gola London",
      bio1: "Aaron Rutigliano is a hospitality entrepreneur, brand strategist, and founder of Gola London, with over 20 years of international experience across London, Rome, and the UAE.",
      bio2: "After years working at the intersection of hospitality, live entertainment, VIP relations, and business development, Aaron identified one major global inefficiency:",
      problem1: "Venues struggle with staffing, guest engagement, and social visibility.",
      problem2: "Artists struggle with monetization and scalable opportunities.",
      bridge: "Buskers on the Table was created to solve both.",
      closing: "By combining hospitality operations, creator economy dynamics, AI, and QR technology, the platform introduces a completely new business model for restaurants, hotels, bars, and entertainment venues.",
      credibility: [
        { t: "20+ Years", d: "Hospitality experience" },
        { t: "London • Rome • UAE", d: "International footprint" },
        { t: "Hospitality × Entertainment × Technology", d: "Cross-industry expertise" },
      ],
      footerFounded: "Founded by Aaron Rutigliano",
    },
    market: {
      cards: [
        { m: "$4.9T", t: "Global Hospitality Market" },
        { m: "$250B", t: "Creator Economy" },
        { m: "24/7", t: "Revenue Opportunity" },
      ],
    },
    nav: { restaurant: "Restaurant", artist: "Artist", admin: "Admin", home: "Home" },
    qrp: {
      live: "Now Performing Live",
      liveAt: "Live at",
      ctaTip: "Tip Artist", ctaTipSub: "£5 · £10 · £20",
      ctaOrder: "Order Food & Drinks", ctaOrderSub: "To your table",
      ctaFollow: "Follow Socials", ctaFollowSub: "IG · TikTok · YT",
      ctaBook: "Request a Private Event", ctaBookSub: "Birthdays · Parties",
      followTitle: "Follow the artist",
      orderTitle: "Order to your table",
      reviewsTitle: "Guest reviews",
      noReviews: "Be the first to review.",
      leaveReview: "Leave a review",
      tipShort: "Tip",
      payWith: "Pay with", secured: "Card · Secured by Stripe",
      confirming: "Confirming your tip…",
      thankYou: "Thank you!",
      thankSub: "Your tip means the world to",
      backToShow: "Back to the show",
      payFailed: "Payment not completed",
      payFailedSub: "No charge was made. Feel free to try again.",
      close: "Close",
      custom: "Custom",
      enterAmount: "Enter amount",
      msgPlaceholder: "Add a message (optional)",
      continuePay: "Continue to secure payment",
      applePay: "Apple Pay", googlePay: "Google Pay", card: "Card",
      opening: "Opening",
      linkCopied: "Link copied to share",
      added: "added",
      orderPlaced: "Order placed",
      placeOrder: "Place order",
      demo: "(demo)",
      bookPre: "Book", bookSuf: "for a private event",
      from: "From", privateSet: "3h private set",
      sendRequest: "Send request",
      pickDate: "Pick a date",
      privateReqSent: "Private event request sent!",
      reviewTitle: "Leave a review",
      reviewPlaceholder: "How was the performance?",
      postReview: "Post review",
      reviewPosted: "Review posted!",
      writeReview: "Write a short review",
      enterMin: "Enter an amount of £1 or more",
      couldNotPay: "Could not start payment",
    },
    footer: { tagline: "Where street talent meets fine hospitality · Demo experience" },
  },

  it: {
    hero: {
      eyebrow: "Buskers on the Table · Dove l'ospitalità incontra l'intrattenimento dal vivo",
      h1a: "Trasforma i Tavoli Vuoti in ",
      h1accent: "Ricavi e Intrattenimento",
      tagline: "La prima forza lavoro dell'ospitalità basata sull'IA, costruita da creator, performer e intrattenitori.",
      sub: "Buskers on the Table trasforma gli artisti in talenti dell'ospitalità, aumentando i ricavi del locale, il coinvolgimento degli ospiti e la visibilità social.",
      audience: "Pensato per artisti di strada, cantanti, performer, creator TikTok, YouTuber e intrattenitori digitali.",
      qrPre: "Ogni performer indossa una ",
      qrBold: "maglia con QR personale",
      qrPost: " — una scansione permette agli ospiti di mandare mance, seguire, ordinare e prenotare.",
      metrics: [
        { value: "1,250+", label: "Artisti registrati" },
        { value: "85+", label: "Locali attivati" },
        { value: "€42k", label: "Mance elaborate" },
      ],
      ctaVenue: "Unisciti come Locale",
      ctaArtist: "Unisciti come Artista",
    },
    problem: {
      eyebrow: "Il Problema",
      title: "L'ospitalità ha poco personale, poco coinvolgimento e poca monetizzazione.",
      subtitle: "I locali investono cifre enormi nell'atmosfera ma non catturano quasi nulla del valore che i momenti dal vivo creano.",
      cards: [
        { t: "Carenza di personale", d: "Le sale sono a corto di staff. Gli ospiti aspettano di più, si sentono meno curati e spendono meno.", stat: "1 su 3", cap: "posti nell'ospitalità scoperti" },
        { t: "Basso coinvolgimento dei clienti", d: "I clienti scorrono il telefono invece di vivere il locale — visite ripetute e fedeltà si bloccano.", stat: "-42%", cap: "tempo di permanenza vs 10 anni fa" },
        { t: "Monetizzazione limitata dalla musica dal vivo", d: "Gli spettacoli dal vivo sono un costo: cari da ingaggiare, impossibili da misurare, senza ritorno dall'atmosfera che creano.", stat: "€0", cap: "ricavi tracciati per set" },
      ],
      infoEyebrow: "Il Problema. L'Opportunità.",
      infoCaption: "Un'unica piattaforma che trasforma il problema in valore per tutti — artisti, ristoranti e ospiti.",
    },
    how: {
      eyebrow: "Come Funziona",
      title: "Dalla richiesta al ricavo in quattro passi.",
      steps: [
        { t: "I ristoranti richiedono staff e performer", d: "I locali pubblicano turni, stili e atmosfera desiderati — da un solo cantante a un intero team di sala." },
        { t: "Gli artisti vengono abbinati con l'IA", d: "La nostra IA abbina ogni locale agli intrattenitori giusti per stile, lingua, valutazione e disponibilità." },
        { t: "Gli artisti si esibiscono + supportano la sala", d: "Talenti ibridi formati offrono momenti dal vivo e aiutano il servizio — intrattenimento e supporto di sala insieme." },
        { t: "Gli ospiti scansionano la maglia QR dell'artista", d: "Il QR stampato sulla divisa di ogni performer permette di ordinare, mandare mance, seguire e prenotare con un tap." },
      ],
      calloutEyebrow: "L'innovazione chiave",
      calloutPre: "I performer diventano talenti ibridi dell'ospitalità: ",
      calloutAccent: "intrattenimento + servizio.",
      calloutSub: "Gli artisti non si limitano a esibirsi — supportano la sala, coinvolgono gli ospiti e migliorano il servizio, tutto insieme.",
    },
    qr: {
      eyebrow: "Innovazione Chiave · La Maglia QR",
      title: "Una maglia. Coinvolgimento infinito.",
      subtitle: "Ogni artista, cantante e creator indossa una divisa brandizzata con un QR personale. Gli ospiti lo scansionano direttamente dalla maglia — trasformando ogni performer in un punto di contatto monetizzabile.",
      unlockTitle: "Una scansione sblocca",
      unlocks: ["Menu digitale del ristorante", "Ordina cibo e bevande", "Manda subito una mancia", "Segui i profili social", "Prenota l'artista per eventi", "Offerte di sponsor e partner"],
      streamsTitle: "Flussi di monetizzazione che sblocca",
      streams: ["Crescita dei ricavi del locale", "Mance digitali", "Prenotazioni artisti", "Ricavi pubblicitari sponsor"],
    },
    solution: {
      eyebrow: "La Soluzione",
      title: "Un ecosistema che trasforma il talento in ricavi.",
      intro: "Buskers on the Table forma artisti di strada, cantanti e creator come intrattenitori pronti per la sala — e li collega ai locali tramite un'unica piattaforma misurabile.",
      points: [
        ["Locali", "ingaggiano intrattenitori selezionati per stile, lingua e disponibilità in pochi secondi — abbinati dall'IA."],
        ["Artisti", "ottengono set retribuiti, mance digitali, prenotazioni private e un pubblico in crescita tramite il loro QR."],
        ["Ospiti", "scansionano, seguono, mandano mance, ordinano e prenotano — trasformando l'atmosfera in coinvolgimento e ricavi."],
        ["Ogni momento", "è tracciato: scansioni, mance, follower, reach e ROI in un'unica dashboard."],
      ],
      cards: [
        { t: "Abbinamento IA", d: "Artista giusto, sala giusta, ogni sera." },
        { t: "Maglia QR", d: "QR indossabile su ogni divisa — il motore di mance e follow che gli ospiti non possono ignorare." },
        { t: "Analisi in tempo reale", d: "Scansioni, mance, reach e ROI, in tempo reale." },
        { t: "Nuovi ricavi", d: "Mance, prenotazioni e conti più alti." },
      ],
      benefitsTitle: "Vantaggi per tutti",
      groups: [
        { label: "Per i Locali", items: [{ t: "Riempi le serate tranquille", d: "Trasforma i turni lenti in esperienze di cui parlare." }, { t: "Nuove fonti di ricavo", d: "Mance, prenotazioni private e conti più alti per tavolo." }, { t: "Reach social gratuita", d: "I creator promuovono il tuo locale ai loro follower." }] },
        { label: "Per gli Artisti", items: [{ t: "Guadagna su ogni set", d: "Cachet + mance digitali + prenotazioni per eventi privati." }, { t: "Fai crescere il pubblico", d: "Il tuo QR converte gli ospiti in follower all'istante." }, { t: "Costruisci una reputazione", d: "Raccogli recensioni dei locali e feedback degli ospiti." }] },
        { label: "Per gli Ospiti", items: [{ t: "Segui all'istante", d: "Instagram, TikTok, YouTube e Spotify con un tap." }, { t: "Manda una mancia al volo", d: "Premia l'esibizione senza contanti." }, { t: "Prenota per eventi", d: "Ingaggia il tuo artista preferito per serate private." }] },
      ],
    },
    business: {
      eyebrow: "Modello di Business",
      title: "Tre fonti di ricavo. Un unico volano.",
      subtitle: "Ricavi software ricorrenti, moltiplicati da una commissione su ogni mancia e prenotazione abilitata dalla piattaforma.",
      cards: [
        { tag: "Ricorrente", t: "Abbonamenti SaaS", d: "Piani mensili a livelli per i locali — dashboard, abbinamento IA, analisi e strumenti QR.", metric: "£49–£299", cap: "per locale / mese" },
        { tag: "Transazionale", t: "Commissione sulle mance", d: "Una piccola percentuale su ogni mancia digitale inviata agli artisti tramite la piattaforma.", metric: "5%", cap: "per mancia" },
        { tag: "Transazionale", t: "Commissione sulle prenotazioni", d: "Commissione della piattaforma su ogni prenotazione ed evento privato chiuso su Buskers.", metric: "15%", cap: "per prenotazione" },
      ],
      captionPre: "Ogni locale porta più artisti → più scansioni QR → più mance e prenotazioni → ",
      captionAccent: "più ricavi per la piattaforma.",
    },
    whyWin: {
      eyebrow: "Perché Vinciamo",
      title: "Una piattaforma che risolve quattro problemi insieme.",
      subtitle: "Nessun concorrente unisce personale, intrattenimento, reach social e sponsorizzazioni in un unico prodotto misurabile.",
      pillars: [
        { t: "Risolve la carenza di personale", d: "Gli intrattenitori formati fanno anche da supporto in sala nelle serate di punta.", metric: "-30%", cap: "pressione in sala nei picchi" },
        { t: "Crea intrattenimento", d: "Momenti dal vivo memorabili — che trasformano i coperti ordinari in un'esperienza.", metric: "+35%", cap: "tempo di permanenza per visita" },
        { t: "Genera visibilità social", d: "Ogni artista promuove il locale ai suoi follower e ogni QR converte gli ospiti in fan.", metric: "12x", cap: "reach social organica" },
        { t: "Sblocca ricavi da sponsor", d: "I brand fanno pubblicità nella pagina digitale di ogni artista — un ricavo mai avuto prima.", metric: "Nuovi €", cap: "ricavi da sponsor per locale" },
      ],
      aiTitle: "Potenziato dal nostro vantaggio IA",
      ai: [
        { t: "Punteggio IA dei Performer", d: "Valuta gli artisti su coinvolgimento, mance e crescita social.", status: "Live" },
        { t: "Previsione IA della Domanda", d: "Prevede le serate e gli artisti che massimizzano i ricavi.", status: "Beta" },
        { t: "Motore di Abbinamento IA", d: "Abbina sempre l'artista giusto alla sala giusta.", status: "Live" },
      ],
      aiNote: "I nostri dati proprietari — scansioni, mance, follower e prenotazioni — addestrano modelli che nessun concorrente può replicare.",
    },
    scale: {
      eyebrow: "Perché Può Scalare a Livello Globale",
      title: "Domanda mondiale. Offerta infinita. Tempismo perfetto.",
      subtitle: "Siamo dove convergono tre mercati in forte crescita — e ogni ingrediente per una piattaforma globale esiste già.",
      cards: [
        { m: "$4.7T", t: "Ospitalità globale", d: "Oltre 15M di ristoranti, bar e hotel in cerca di differenziazione e maggiore spesa." },
        { m: "$250B", t: "Creator economy", d: "Milioni di performer che monetizzano momenti reali — e in forte crescita." },
        { m: "$35B", t: "Intrattenimento dal vivo", d: "La domanda di esperienze dal vivo cresce più veloce di ogni altra categoria." },
        { m: "Prossima ondata", t: "Ospitalità potenziata dall'IA", d: "Abbinamento e previsione IA sono esattamente dove sta andando l'ospitalità." },
      ],
      bannerLabel: "Mercato servibile — locali dell'ospitalità con programmazione dal vivo, a livello globale",
      bannerTitle: "Una categoria senza una piattaforma dedicata. Fino ad ora.",
      bannerValue: "$18B+",
      disclaimer: "Le cifre di mercato sono stime illustrative a scopo dimostrativo.",
    },
    cta: {
      eyebrow: "Prenota una Demo",
      title: "Il Futuro dell'Ospitalità Inizia Qui.",
      sub: "Buskers on the Table sta ridefinendo il modo in cui i ristoranti attirano ospiti, coinvolgono il pubblico e creano ricavi.",
      book: "Prenota una Demo",
      artist: "Unisciti come Artista",
    },
    values: {
      title: "Un vero motore di crescita completo",
      subtitle: "Tre sistemi che lavorano come uno — trasformano i momenti dal vivo in ricavi misurabili.",
      cards: [
        { t: "Piattaforma con IA", d: "Abbinamento intelligente, scoring delle performance e previsione della domanda: l'artista giusto nella sala giusta, ogni sera." },
        { t: "Motore di Mance Digitali", d: "Una scansione QR permette agli ospiti di lasciare una mancia con Apple Pay, Google Pay o carta — nuovi ricavi su ogni set." },
        { t: "Sistema di Crescita Social", d: "Ogni performer converte gli ospiti in follower, moltiplicando la reach organica per artisti e locali." },
      ],
    },
    whyNow: {
      eyebrow: "Perché Ora",
      title: "Il momento dell'intrattenimento nell'ospitalità è adesso.",
      problemTitle: "Il Problema",
      restaurantsLabel: "I ristoranti affrontano",
      restaurants: ["Carenza di personale", "Minor coinvolgimento degli ospiti", "Costi operativi in aumento"],
      artistsLabel: "Gli artisti affrontano",
      artists: ["Reddito instabile", "Visibilità limitata", "Nessun percorso di carriera strutturato"],
      solutionTitle: "La Soluzione",
      solutionLabel: "Buskers on the Table unisce",
      solutions: ["Ospitalità", "Intrattenimento dal vivo", "Tecnologia IA", "Crescita Social"],
      statement1: "Trasforma lo staff di sala in generatori di ricavi.",
      statement2: "Trasforma i performer in asset scalabili per l'ospitalità.",
    },
    forWho: {
      eyebrow: "Per Chi",
      title: "Pensato per chiunque plasmi l'esperienza dell'ospite.",
      cards: [
        { t: "Ristoranti e Locali", d: "Aumenta i ricavi, migliora il coinvolgimento dei clienti e trasforma l'esperienza dell'ospitalità." },
        { t: "Artisti e Performer", d: "Genera reddito, costruisci il tuo pubblico e fai crescere la tua presenza digitale." },
        { t: "Gruppi dell'Ospitalità", d: "Attiva una crescita scalabile guidata dall'intrattenimento su più locali." },
      ],
    },
    leadForm: {
      ctaVenue: "Unisciti come Locale", ctaArtist: "Unisciti come Artista", requestDemo: "Richiedi una Demo", bookCall: "Prenota una Chiamata",
      titleVenue: "Unisciti come Locale", titleArtist: "Unisciti come Artista", titleDemo: "Richiedi una Demo", titleCall: "Prenota una Chiamata",
      subtitle: "Raccontaci qualcosa di te e il nostro team ti contatterà.",
      fullName: "Nome completo", email: "Email", phone: "Telefono (opzionale)", company: "Azienda / Nome del Locale",
      role: "Ruolo", roleVenue: "Locale", roleArtist: "Artista", roleInvestor: "Investitore", roleGroup: "Gruppo dell'Ospitalità",
      country: "Paese", message: "Messaggio", messagePlaceholder: "Come possiamo aiutarti?",
      submit: "Invia", submitting: "Invio…",
      successTitle: "Grazie.", successMsg: "Il nostro team ti contatterà a breve.", close: "Chiudi",
      errRequired: "Inserisci nome ed email.", errEmail: "Inserisci un'email valida.", errSubmit: "Qualcosa è andato storto. Riprova.",
    },
    dash: {
      r: { eyebrow: "Dashboard Ristorante", bookings: "Prenotazioni", qrScans: "Scansioni QR", tipsGenerated: "Mance generate", socialReach: "Reach Social",
        confirmed: "confermate", thisMonth: "Questo mese", guestTips: "Mance ospiti", impressions: "Impression stimate",
        aiMatching: "Abbinamento IA", runMatch: "Avvia Abbinamento IA", browseArtists: "Esplora Artisti",
        allStyles: "Tutti gli stili", allCities: "Tutte le città", yourBookings: "Le tue prenotazioni", noBookings: "Ancora nessuna prenotazione.", book: "Prenota", seats: "posti" },
      a: { eyebrow: "Dashboard Artista", welcome: "Bentornato", personalQr: "Il tuo QR personale", copy: "Copia", preview: "Anteprima",
        aiScore: "Punteggio Performance IA", analyseMe: "Analizzami", totalEarnings: "Guadagni totali", tipsReceived: "Mance ricevute",
        followersGained: "Follower guadagnati", rating: "Valutazione", followerGrowth: "Crescita follower", aiBio: "Generatore Bio IA",
        generateBio: "Genera nuova bio", bookingRequests: "Richieste di prenotazione", noRequests: "Nessuna richiesta.", accept: "Accetta",
        decline: "Rifiuta", recentTips: "Mance recenti", noTips: "Ancora nessuna mancia.", reviews: "Recensioni", noReviews: "Ancora nessuna recensione." },
      admin: { eyebrow: "Console Admin", title: "Panoramica Piattaforma", venues: "Locali", artists: "Artisti", bookings: "Prenotazioni",
        grossBookings: "Prenotazioni lorde", platformRevenue: "Ricavi Piattaforma", pendingApprovals: "Approvazioni in sospeso",
        allReviewed: "Tutti i profili sono stati esaminati.", approve: "Approva", reject: "Rifiuta", topArtists: "Artisti migliori (per mance)",
        byCity: "Prenotazioni per città", mostActive: "Locali più attivi", leaderboard: "Classifica", bookingsWord: "prenotazioni" },
    },
    founder: {
      badgeLine: "Costruito dall'ospitalità, per l'ospitalità.",
      badgeRole: "CEO e Fondatore",
      name: "Aaron Rutigliano",
      eyebrow: "Incontra il Fondatore",
      role: "CEO e Fondatore · Fondatore di Gola London",
      bio1: "Aaron Rutigliano è un imprenditore dell'ospitalità, brand strategist e fondatore di Gola London, con oltre 20 anni di esperienza internazionale tra Londra, Roma e gli Emirati Arabi Uniti.",
      bio2: "Dopo anni di lavoro all'incrocio tra ospitalità, intrattenimento dal vivo, relazioni VIP e sviluppo commerciale, Aaron ha individuato una grande inefficienza globale:",
      problem1: "I locali faticano con personale, coinvolgimento degli ospiti e visibilità social.",
      problem2: "Gli artisti faticano con la monetizzazione e le opportunità scalabili.",
      bridge: "Buskers on the Table è nato per risolvere entrambi.",
      closing: "Combinando operazioni dell'ospitalità, dinamiche della creator economy, IA e tecnologia QR, la piattaforma introduce un modello di business completamente nuovo per ristoranti, hotel, bar e locali di intrattenimento.",
      credibility: [
        { t: "20+ Anni", d: "Esperienza nell'ospitalità" },
        { t: "Londra • Roma • Emirati", d: "Presenza internazionale" },
        { t: "Ospitalità × Intrattenimento × Tecnologia", d: "Competenza cross-settore" },
      ],
      footerFounded: "Fondata da Aaron Rutigliano",
    },
    market: {
      cards: [
        { m: "$4.9T", t: "Mercato Globale dell'Ospitalità" },
        { m: "$250B", t: "Creator Economy" },
        { m: "24/7", t: "Opportunità di Ricavo" },
      ],
    },
    nav: { restaurant: "Ristorante", artist: "Artista", admin: "Admin", home: "Inizio" },
    qrp: {
      live: "Ora dal vivo",
      liveAt: "Dal vivo a",
      ctaTip: "Manda mancia", ctaTipSub: "£5 · £10 · £20",
      ctaOrder: "Ordina cibo e bevande", ctaOrderSub: "Al tuo tavolo",
      ctaFollow: "Segui sui social", ctaFollowSub: "IG · TikTok · YT",
      ctaBook: "Richiedi un evento privato", ctaBookSub: "Compleanni · Feste",
      followTitle: "Segui l'artista",
      orderTitle: "Ordina al tuo tavolo",
      reviewsTitle: "Recensioni degli ospiti",
      noReviews: "Sii il primo a recensire.",
      leaveReview: "Lascia una recensione",
      tipShort: "Mancia a",
      payWith: "Paga con", secured: "Carta · Protetto da Stripe",
      confirming: "Confermiamo la tua mancia…",
      thankYou: "Grazie!",
      thankSub: "La tua mancia significa tanto per",
      backToShow: "Torna allo spettacolo",
      payFailed: "Pagamento non completato",
      payFailedSub: "Nessun addebito effettuato. Riprova pure.",
      close: "Chiudi",
      custom: "Personalizza",
      enterAmount: "Inserisci importo",
      msgPlaceholder: "Aggiungi un messaggio (opzionale)",
      continuePay: "Vai al pagamento sicuro",
      applePay: "Apple Pay", googlePay: "Google Pay", card: "Carta",
      opening: "Apertura",
      linkCopied: "Link copiato per condividere",
      added: "aggiunto",
      orderPlaced: "Ordine effettuato",
      placeOrder: "Effettua ordine",
      demo: "(demo)",
      bookPre: "Prenota", bookSuf: "per un evento privato",
      from: "Da", privateSet: "set privato di 3h",
      sendRequest: "Invia richiesta",
      pickDate: "Scegli una data",
      privateReqSent: "Richiesta di evento privato inviata!",
      reviewTitle: "Lascia una recensione",
      reviewPlaceholder: "Com'è stata l'esibizione?",
      postReview: "Pubblica recensione",
      reviewPosted: "Recensione pubblicata!",
      writeReview: "Scrivi una breve recensione",
      enterMin: "Inserisci un importo di £1 o più",
      couldNotPay: "Impossibile avviare il pagamento",
    },
    footer: { tagline: "Dove il talento di strada incontra l'alta ospitalità · Esperienza demo" },
  },

  es: {
    hero: {
      eyebrow: "Buskers on the Table · Donde la hostelería se une al entretenimiento en vivo",
      h1a: "Convierte Mesas Vacías en ",
      h1accent: "Ingresos y Entretenimiento",
      sub: "Buskers on the Table transforma a los artistas en talento hostelero, aumentando los ingresos del local, la interacción de los clientes y el alcance social.",
      audience: "Creado para artistas callejeros, cantantes, performers, creadores de TikTok, YouTubers y entretenedores digitales.",
      qrPre: "Cada performer lleva una ",
      qrBold: "camiseta con QR personal",
      qrPost: " — un escaneo permite a los clientes dar propina, seguir, pedir y reservar.",
      metrics: [
        { value: "1,250+", label: "Artistas registrados" },
        { value: "85+", label: "Locales activados" },
        { value: "€42k", label: "Propinas procesadas" },
      ],
      ctaVenue: "Únete como Local",
      ctaArtist: "Únete como Artista",
    },
    problem: {
      eyebrow: "El Problema",
      title: "La hostelería tiene poco personal, poca interacción y poca monetización.",
      subtitle: "Los locales invierten fortunas en ambiente pero apenas capturan el valor que crean sus momentos en vivo.",
      cards: [
        { t: "Escasez de personal", d: "Las salas van cortas de personal. Los clientes esperan más, se sienten menos atendidos y gastan menos.", stat: "1 de 3", cap: "puestos de hostelería sin cubrir" },
        { t: "Baja interacción del cliente", d: "Los clientes miran el móvil en vez de vivir el local — las visitas repetidas y la fidelidad se estancan.", stat: "-42%", cap: "tiempo de permanencia vs hace 10 años" },
        { t: "Monetización limitada de la música en vivo", d: "Los shows en vivo son un coste: caros de contratar, imposibles de medir, sin retorno del ambiente que crean.", stat: "€0", cap: "ingresos registrados por set" },
      ],
      infoEyebrow: "El Problema. La Oportunidad.",
      infoCaption: "Una sola plataforma que convierte el problema en valor para todos — artistas, restaurantes y clientes.",
    },
    how: {
      eyebrow: "Cómo Funciona",
      title: "De la solicitud al ingreso en cuatro pasos.",
      steps: [
        { t: "Los restaurantes solicitan personal y performers", d: "Los locales publican los turnos, estilos y ambiente que necesitan — desde un cantante solo hasta un equipo de sala completo." },
        { t: "Los artistas se emparejan con IA", d: "Nuestra IA empareja cada local con los entretenedores adecuados por estilo, idioma, valoración y disponibilidad." },
        { t: "Los artistas actúan + apoyan la sala", d: "Talento híbrido formado ofrece momentos en vivo y ayuda al servicio — entretenimiento y apoyo de sala en uno." },
        { t: "Los clientes escanean la camiseta QR del artista", d: "El QR impreso en el uniforme de cada performer permite pedir, dar propina, seguir y reservar con un toque." },
      ],
      calloutEyebrow: "La innovación clave",
      calloutPre: "Los performers se convierten en talento hostelero híbrido: ",
      calloutAccent: "entretenimiento + servicio.",
      calloutSub: "Los artistas no solo actúan — apoyan la sala, involucran a los clientes y mejoran el servicio, todo a la vez.",
    },
    qr: {
      eyebrow: "Innovación Clave · La Camiseta QR",
      title: "Una camiseta. Interacción infinita.",
      subtitle: "Cada artista, cantante y creador lleva un uniforme de marca con un QR personal. Los clientes lo escanean directamente de la camiseta — convirtiendo a cada performer en un punto de contacto monetizable.",
      unlockTitle: "Un escaneo desbloquea",
      unlocks: ["Menú digital del restaurante", "Pedir comida y bebidas", "Dar propina al instante", "Seguir perfiles sociales", "Reservar al artista para eventos", "Ofertas de patrocinadores y socios"],
      streamsTitle: "Flujos de monetización que desbloquea",
      streams: ["Crecimiento de ingresos del local", "Propinas digitales", "Reservas de artistas", "Ingresos por publicidad de patrocinadores"],
    },
    solution: {
      eyebrow: "La Solución",
      title: "Un ecosistema que convierte el talento en ingresos.",
      intro: "Buskers on the Table forma a artistas callejeros, cantantes y creadores como entretenedores listos para la sala — y los conecta con locales mediante una única plataforma medible.",
      points: [
        ["Locales", "contratan entretenedores verificados por estilo, idioma y disponibilidad en segundos — emparejados por IA."],
        ["Artistas", "obtienen sets pagados, propinas digitales, reservas privadas y un público creciente a través de su QR."],
        ["Clientes", "escanean, siguen, dan propina, piden y reservan — convirtiendo el ambiente en interacción e ingresos."],
        ["Cada momento", "se registra: escaneos, propinas, seguidores, alcance y ROI en un solo panel."],
      ],
      cards: [
        { t: "Emparejamiento IA", d: "Artista correcto, sala correcta, cada noche." },
        { t: "Camiseta QR", d: "QR ponible en cada uniforme — el motor de propinas y seguidores que los clientes no pueden ignorar." },
        { t: "Analítica en vivo", d: "Escaneos, propinas, alcance y ROI, en tiempo real." },
        { t: "Nuevos ingresos", d: "Propinas, reservas y cuentas más altas." },
      ],
      benefitsTitle: "Beneficios para todos",
      groups: [
        { label: "Para Locales", items: [{ t: "Llena las noches tranquilas", d: "Convierte los turnos lentos en experiencias de las que se habla." }, { t: "Nuevas líneas de ingreso", d: "Propinas, reservas privadas y cuentas más altas por mesa." }, { t: "Alcance social gratis", d: "Los creadores promocionan tu local a sus seguidores." }] },
        { label: "Para Artistas", items: [{ t: "Gana en cada set", d: "Caché + propinas digitales + reservas de eventos privados." }, { t: "Haz crecer tu público", d: "Tu QR convierte a los clientes en seguidores al instante." }, { t: "Construye reputación", d: "Reúne reseñas de locales y opiniones de clientes." }] },
        { label: "Para Clientes", items: [{ t: "Sigue al instante", d: "Instagram, TikTok, YouTube y Spotify con un toque." }, { t: "Da propina al momento", d: "Premia la actuación sin efectivo." }, { t: "Reserva para eventos", d: "Contrata a tu artista favorito para noches privadas." }] },
      ],
    },
    business: {
      eyebrow: "Modelo de Negocio",
      title: "Tres fuentes de ingreso. Un solo volante.",
      subtitle: "Ingresos de software recurrentes, multiplicados por una comisión en cada propina y reserva que habilita la plataforma.",
      cards: [
        { tag: "Recurrente", t: "Suscripciones SaaS", d: "Planes mensuales por niveles para locales — paneles, emparejamiento IA, analítica y herramientas QR.", metric: "£49–£299", cap: "por local / mes" },
        { tag: "Transaccional", t: "Comisión en propinas", d: "Una pequeña comisión sobre cada propina digital que los clientes envían a los artistas.", metric: "5%", cap: "por propina" },
        { tag: "Transaccional", t: "Comisión en reservas", d: "Comisión de la plataforma en cada reserva de artista y evento privado cerrado en Buskers.", metric: "15%", cap: "por reserva" },
      ],
      captionPre: "Cada local atrae más artistas → más escaneos QR → más propinas y reservas → ",
      captionAccent: "más ingresos para la plataforma.",
    },
    whyWin: {
      eyebrow: "Por Qué Ganamos",
      title: "Una plataforma que resuelve cuatro problemas a la vez.",
      subtitle: "Ningún competidor combina personal, entretenimiento, alcance social y patrocinio en un solo producto medible.",
      pillars: [
        { t: "Resuelve la escasez de personal", d: "Los entretenedores formados también apoyan la sala en las noches de mayor demanda.", metric: "-30%", cap: "presión de sala en horas punta" },
        { t: "Crea entretenimiento", d: "Momentos en vivo memorables — que convierten cuentas ordinarias en una experiencia.", metric: "+35%", cap: "tiempo de permanencia por visita" },
        { t: "Genera visibilidad social", d: "Cada artista promociona el local a sus seguidores y cada QR convierte clientes en fans.", metric: "12x", cap: "alcance social orgánico" },
        { t: "Desbloquea ingresos por patrocinio", d: "Las marcas se anuncian en la página digital de cada artista — un ingreso que ningún local tenía antes.", metric: "Nuevos €", cap: "ingresos por patrocinio por local" },
      ],
      aiTitle: "Impulsado por nuestra ventaja en IA",
      ai: [
        { t: "Puntuación IA de Performers", d: "Califica a los artistas por interacción, propinas y impulso social.", status: "Live" },
        { t: "Predicción IA de Demanda", d: "Predice las noches y artistas que maximizan los ingresos.", status: "Beta" },
        { t: "Motor de Emparejamiento IA", d: "Empareja siempre al artista correcto con la sala correcta.", status: "Live" },
      ],
      aiNote: "Nuestros datos propios — escaneos, propinas, seguidores y reservas — entrenan modelos que ningún competidor puede replicar.",
    },
    scale: {
      eyebrow: "Por Qué Puede Escalar Globalmente",
      title: "Demanda mundial. Oferta infinita. Momento perfecto.",
      subtitle: "Estamos donde convergen tres mercados en auge — y todos los ingredientes para una plataforma global ya existen.",
      cards: [
        { m: "$4.7T", t: "Hostelería global", d: "Más de 15M de restaurantes, bares y hoteles buscando diferenciarse y aumentar el gasto." },
        { m: "$250B", t: "Creator economy", d: "Millones de performers monetizando momentos reales — y creciendo rápido." },
        { m: "$35B", t: "Entretenimiento en vivo", d: "La demanda de experiencias en vivo crece más rápido que cualquier otra categoría." },
        { m: "Próxima ola", t: "Hostelería con IA", d: "El emparejamiento y la predicción con IA son justo hacia donde va la hostelería." },
      ],
      bannerLabel: "Mercado abordable — locales de hostelería con programación en vivo, a nivel global",
      bannerTitle: "Una categoría sin plataforma dedicada. Hasta ahora.",
      bannerValue: "$18B+",
      disclaimer: "Las cifras de mercado son estimaciones ilustrativas con fines demostrativos.",
    },
    cta: {
      eyebrow: "Reserva una Demo",
      title: "El Futuro de la Hostelería Empieza Aquí.",
      sub: "Buskers on the Table está redefiniendo cómo los restaurantes atraen clientes, involucran audiencias y crean ingresos.",
      book: "Reserva una Demo",
      artist: "Únete como Artista",
    },
    values: {
      title: "Un auténtico motor de crecimiento completo",
      subtitle: "Tres sistemas funcionando como uno — convierten los momentos en vivo en ingresos medibles.",
      cards: [
        { t: "Plataforma con IA", d: "Emparejamiento inteligente, puntuación de rendimiento y predicción de demanda: el artista adecuado en la sala adecuada, cada noche." },
        { t: "Motor de Propinas Digitales", d: "Un escaneo QR permite a los clientes dar propina al instante con Apple Pay, Google Pay o tarjeta — nuevos ingresos en cada set." },
        { t: "Sistema de Crecimiento Social", d: "Cada performer convierte a los clientes en seguidores, multiplicando el alcance orgánico para artistas y locales." },
      ],
    },
    whyNow: {
      eyebrow: "Por Qué Ahora",
      title: "El momento del entretenimiento en la hostelería es ahora.",
      problemTitle: "El Problema",
      restaurantsLabel: "Los restaurantes enfrentan",
      restaurants: ["Escasez de personal", "Menor interacción del cliente", "Costes operativos crecientes"],
      artistsLabel: "Los artistas enfrentan",
      artists: ["Ingresos inestables", "Visibilidad limitada", "Sin una carrera estructurada"],
      solutionTitle: "La Solución",
      solutionLabel: "Buskers on the Table combina",
      solutions: ["Hostelería", "Entretenimiento en vivo", "Tecnología IA", "Crecimiento Social"],
      statement1: "Convierte al personal de sala en generadores de ingresos.",
      statement2: "Convierte a los performers en activos escalables para la hostelería.",
    },
    forWho: {
      eyebrow: "Para Quién",
      title: "Creado para todos los que dan forma a la experiencia del cliente.",
      cards: [
        { t: "Restaurantes y Locales", d: "Aumenta los ingresos, mejora la interacción del cliente y transforma la experiencia hostelera." },
        { t: "Artistas y Performers", d: "Genera ingresos, construye tu audiencia y haz crecer tu presencia digital." },
        { t: "Grupos de Hostelería", d: "Despliega un crecimiento escalable impulsado por el entretenimiento en varios locales." },
      ],
    },
    leadForm: {
      ctaVenue: "Únete como Local", ctaArtist: "Únete como Artista", requestDemo: "Solicitar una Demo", bookCall: "Reservar una Llamada",
      titleVenue: "Únete como Local", titleArtist: "Únete como Artista", titleDemo: "Solicitar una Demo", titleCall: "Reservar una Llamada",
      subtitle: "Cuéntanos un poco sobre ti y nuestro equipo se pondrá en contacto.",
      fullName: "Nombre completo", email: "Email", phone: "Teléfono (opcional)", company: "Empresa / Nombre del Local",
      role: "Rol", roleVenue: "Local", roleArtist: "Artista", roleInvestor: "Inversor", roleGroup: "Grupo de Hostelería",
      country: "País", message: "Mensaje", messagePlaceholder: "¿Cómo podemos ayudarte?",
      submit: "Enviar", submitting: "Enviando…",
      successTitle: "Gracias.", successMsg: "Nuestro equipo se pondrá en contacto contigo en breve.", close: "Cerrar",
      errRequired: "Introduce tu nombre y email.", errEmail: "Introduce un email válido.", errSubmit: "Algo salió mal. Inténtalo de nuevo.",
    },
    dash: {
      r: { eyebrow: "Panel de Restaurante", bookings: "Reservas", qrScans: "Escaneos QR", tipsGenerated: "Propinas generadas", socialReach: "Alcance Social",
        confirmed: "confirmadas", thisMonth: "Este mes", guestTips: "Propinas de clientes", impressions: "Impresiones estimadas",
        aiMatching: "Emparejamiento IA", runMatch: "Ejecutar Emparejamiento IA", browseArtists: "Explorar Artistas",
        allStyles: "Todos los estilos", allCities: "Todas las ciudades", yourBookings: "Tus reservas", noBookings: "Aún no hay reservas.", book: "Reservar", seats: "plazas" },
      a: { eyebrow: "Panel de Artista", welcome: "Bienvenido de nuevo", personalQr: "Tu QR personal", copy: "Copiar", preview: "Vista previa",
        aiScore: "Puntuación de Rendimiento IA", analyseMe: "Analízame", totalEarnings: "Ingresos totales", tipsReceived: "Propinas recibidas",
        followersGained: "Seguidores ganados", rating: "Valoración", followerGrowth: "Crecimiento de seguidores", aiBio: "Generador de Bio IA",
        generateBio: "Generar nueva bio", bookingRequests: "Solicitudes de reserva", noRequests: "Sin solicitudes de reserva.", accept: "Aceptar",
        decline: "Rechazar", recentTips: "Propinas recientes", noTips: "Aún no hay propinas.", reviews: "Reseñas", noReviews: "Aún no hay reseñas." },
      admin: { eyebrow: "Consola de Admin", title: "Visión de la Plataforma", venues: "Locales", artists: "Artistas", bookings: "Reservas",
        grossBookings: "Reservas brutas", platformRevenue: "Ingresos de la Plataforma", pendingApprovals: "Aprobaciones pendientes",
        allReviewed: "Todos los perfiles revisados.", approve: "Aprobar", reject: "Rechazar", topArtists: "Mejores artistas (por propinas)",
        byCity: "Reservas por ciudad", mostActive: "Locales más activos", leaderboard: "Clasificación", bookingsWord: "reservas" },
    },
    founder: {
      badgeLine: "Creado por la hostelería, para la hostelería.",
      badgeRole: "CEO y Fundador",
      name: "Aaron Rutigliano",
      eyebrow: "Conoce al Fundador",
      role: "CEO y Fundador · Fundador de Gola London",
      bio1: "Aaron Rutigliano es un emprendedor hostelero, estratega de marca y fundador de Gola London, con más de 20 años de experiencia internacional entre Londres, Roma y los Emiratos Árabes Unidos.",
      bio2: "Tras años trabajando en la intersección de la hostelería, el entretenimiento en vivo, las relaciones VIP y el desarrollo de negocio, Aaron identificó una gran ineficiencia global:",
      problem1: "Los locales luchan con el personal, la interacción del cliente y la visibilidad social.",
      problem2: "Los artistas luchan con la monetización y las oportunidades escalables.",
      bridge: "Buskers on the Table nació para resolver ambos.",
      closing: "Combinando operaciones hosteleras, dinámicas de la creator economy, IA y tecnología QR, la plataforma introduce un modelo de negocio completamente nuevo para restaurantes, hoteles, bares y locales de entretenimiento.",
      credibility: [
        { t: "20+ Años", d: "Experiencia en hostelería" },
        { t: "Londres • Roma • Emiratos", d: "Presencia internacional" },
        { t: "Hostelería × Entretenimiento × Tecnología", d: "Experiencia intersectorial" },
      ],
      footerFounded: "Fundada por Aaron Rutigliano",
    },
    market: {
      cards: [
        { m: "$4.9T", t: "Mercado Global de Hostelería" },
        { m: "$250B", t: "Creator Economy" },
        { m: "24/7", t: "Oportunidad de Ingresos" },
      ],
    },
    nav: { restaurant: "Restaurante", artist: "Artista", admin: "Admin", home: "Inicio" },
    qrp: {
      live: "Actuando en vivo",
      liveAt: "En vivo en",
      ctaTip: "Dar propina", ctaTipSub: "£5 · £10 · £20",
      ctaOrder: "Pedir comida y bebidas", ctaOrderSub: "A tu mesa",
      ctaFollow: "Seguir en redes", ctaFollowSub: "IG · TikTok · YT",
      ctaBook: "Consultar un evento privado", ctaBookSub: "Cumpleaños · Fiestas",
      followTitle: "Sigue al artista",
      orderTitle: "Pide a tu mesa",
      reviewsTitle: "Reseñas de clientes",
      noReviews: "Sé el primero en reseñar.",
      leaveReview: "Deja una reseña",
      tipShort: "Propina a",
      payWith: "Paga con", secured: "Tarjeta · Protegido por Stripe",
      confirming: "Confirmando tu propina…",
      thankYou: "¡Gracias!",
      thankSub: "Tu propina significa mucho para",
      backToShow: "Volver al show",
      payFailed: "Pago no completado",
      payFailedSub: "No se realizó ningún cargo. Puedes intentarlo de nuevo.",
      close: "Cerrar",
      custom: "Personalizar",
      enterAmount: "Introduce importe",
      msgPlaceholder: "Añade un mensaje (opcional)",
      continuePay: "Ir al pago seguro",
      applePay: "Apple Pay", googlePay: "Google Pay", card: "Tarjeta",
      opening: "Abriendo",
      linkCopied: "Enlace copiado para compartir",
      added: "añadido",
      orderPlaced: "Pedido realizado",
      placeOrder: "Realizar pedido",
      demo: "(demo)",
      bookPre: "Reservar", bookSuf: "para un evento privado",
      from: "Desde", privateSet: "set privado de 3h",
      sendRequest: "Enviar solicitud",
      pickDate: "Elige una fecha",
      privateReqSent: "¡Solicitud de evento privado enviada!",
      reviewTitle: "Deja una reseña",
      reviewPlaceholder: "¿Qué te pareció la actuación?",
      postReview: "Publicar reseña",
      reviewPosted: "¡Reseña publicada!",
      writeReview: "Escribe una breve reseña",
      enterMin: "Introduce un importe de £1 o más",
      couldNotPay: "No se pudo iniciar el pago",
    },
    footer: { tagline: "Donde el talento callejero se une a la alta hostelería · Experiencia demo" },
  },
};

for (const [code, text] of Object.entries(content)) {
  const demo=demoCopy[code];
  Object.assign(text.leadForm,{subtitle:demo.formNote,submit:demo.submit,successTitle:demo.successTitle,successMsg:demo.successMsg});
  for (const key of ['continuePay','thankYou','thankSub','confirming','live','liveAt','sendRequest','privateReqSent','reviewPosted']) text.qrp[key]=demo[key];
}

export const LANGS = [
  { code: "en", label: "EN" },
  { code: "it", label: "IT" },
  { code: "es", label: "ES" },
];

const LangContext = createContext({ lang: "en", setLang: () => {}, t: content.en });

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {try {const saved=localStorage.getItem("bott_lang");return ["en","it","es"].includes(saved)?saved:"en";} catch{return "en";}});
  useEffect(() => {
    try {localStorage.setItem("bott_lang", lang);} catch {}
    document.documentElement.lang = lang;
  }, [lang]);
  const value = { lang, setLang, t: content[lang] || content.en };
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
